/**
 * Accreditation Shared Components
 * Reusable components for accreditation UI
 */
import React from 'react';
import { getStatusColor, getStatusLabel, formatDate } from '../api/accreditationService';

// ============================================================================
// STATUS BADGE
// ============================================================================

export const AccreditationStatusBadge = ({ status }) => {
  const color = getStatusColor(status);
  const label = getStatusLabel(status);

  const style = {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '12px',
    backgroundColor: color,
    color: '#fff',
    fontSize: '0.875rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  return <span style={style}>{label}</span>;
};

// ============================================================================
// ACCREDITATION TIMELINE
// ============================================================================

export const AccreditationTimeline = ({ accreditation }) => {
  const timelineStyle = {
    position: 'relative',
    paddingLeft: '30px',
    marginTop: '20px'
  };

  const lineStyle = {
    position: 'absolute',
    left: '8px',
    top: '0',
    bottom: '0',
    width: '2px',
    background: '#e0e0e0'
  };

  const eventStyle = {
    position: 'relative',
    marginBottom: '20px',
    paddingBottom: '20px'
  };

  const dotStyle = (isActive) => ({
    position: 'absolute',
    left: '-26px',
    top: '4px',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: isActive ? '#27ae60' : '#e0e0e0',
    border: `2px solid ${isActive ? '#27ae60' : '#ccc'}`
  });

  const events = [
    {
      label: 'Application Submitted',
      date: accreditation.application_date,
      active: true
    },
    {
      label: 'Under Review',
      date: accreditation.status !== 'applied' ? accreditation.updated_at : null,
      active: accreditation.status !== 'applied'
    },
    {
      label: 'Decision Made',
      date: accreditation.decision_date,
      active: !!accreditation.decision_date
    },
    {
      label: 'Accreditation Granted',
      date: accreditation.granted_date,
      active: !!accreditation.granted_date
    }
  ];

  if (accreditation.expiry_date) {
    events.push({
      label: 'Expires',
      date: accreditation.expiry_date,
      active: false,
      isFuture: true
    });
  }

  return (
    <div style={timelineStyle}>
      <div style={lineStyle}></div>
      {events.map((event, index) => (
        <div key={index} style={eventStyle}>
          <div style={dotStyle(event.active)}></div>
          <div>
            <strong style={{ color: event.active ? '#333' : '#999' }}>
              {event.label}
            </strong>
            <div style={{ color: '#666', fontSize: '0.875rem', marginTop: '4px' }}>
              {event.date ? formatDate(event.date) : event.isFuture ? formatDate(event.date) : 'Pending'}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// SCOPE SELECTOR
// ============================================================================

export const AccreditationScopeSelector = ({ scopes, selectedScopes, onChange }) => {
  const handleToggle = (scopeId) => {
    const newSelection = selectedScopes.includes(scopeId)
      ? selectedScopes.filter(id => id !== scopeId)
      : [...selectedScopes, scopeId];
    onChange(newSelection);
  };

  const containerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '12px',
    marginTop: '12px'
  };

  const scopeCardStyle = (isSelected) => ({
    padding: '16px',
    border: `2px solid ${isSelected ? '#3498db' : '#e0e0e0'}`,
    borderRadius: '8px',
    cursor: 'pointer',
    background: isSelected ? '#e3f2fd' : '#fff',
    transition: 'all 0.2s'
  });

  if (!scopes || scopes.length === 0) {
    return (
      <div style={{
        padding: '16px',
        background: '#f5f5f5',
        border: '1px solid #ddd',
        borderRadius: '4px',
        color: '#666',
        textAlign: 'center'
      }}>
        <p>No accreditation scopes available at this time.</p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {scopes.map((scope) => {
        const isSelected = selectedScopes.includes(scope.id);
        return (
          <div
            key={scope.id}
            style={scopeCardStyle(isSelected)}
            onClick={() => handleToggle(scope.id)}
          >
            <div style={{ fontWeight: '600', color: '#333', marginBottom: '4px' }}>
              {scope.scope_code || scope.code}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              {scope.scope_name || scope.name}
            </div>
            {isSelected && (
              <div style={{ marginTop: '8px', color: '#3498db', fontSize: '0.875rem' }}>
                Selected
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// DECISION FORM
// ============================================================================

export const AccreditationDecisionForm = ({ onSubmit, loading }) => {
  const [newStatus, setNewStatus] = React.useState('granted');
  const [decisionNotes, setDecisionNotes] = React.useState('');
  const [reason, setReason] = React.useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ newStatus, decisionNotes, reason });
  };

  const formStyle = {
    maxWidth: '600px',
    padding: '24px',
    background: '#f9f9f9',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  };

  const fieldStyle = {
    marginBottom: '16px'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#333'
  };

  const selectStyle = {
    width: '100%',
    padding: '10px',
    fontSize: '1rem',
    border: '1px solid #ddd',
    borderRadius: '4px'
  };

  const textareaStyle = {
    width: '100%',
    padding: '10px',
    fontSize: '1rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    minHeight: '100px',
    fontFamily: 'inherit'
  };

  const buttonStyle = {
    padding: '12px 24px',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#fff',
    background: '#27ae60',
    border: 'none',
    borderRadius: '4px',
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.6 : 1
  };

  return (
    <form onSubmit={handleSubmit} style={formStyle}>
      <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Make Accreditation Decision</h3>

      <div style={fieldStyle}>
        <label style={labelStyle}>Decision</label>
        <select
          value={newStatus}
          onChange={(e) => setNewStatus(e.target.value)}
          style={selectStyle}
          required
        >
          <option value="under_review">Move to Under Review</option>
          <option value="document_review">Move to Document Review</option>
          <option value="initial_assessment">Move to Initial Assessment</option>
          <option value="witness_audit">Move to Witness Audit</option>
          <option value="technical_review">Move to Technical Review</option>
          <option value="granted">Grant Accreditation</option>
          <option value="rejected">Reject Application</option>
          <option value="suspended">Suspend Accreditation</option>
          <option value="withdrawn">Withdraw Accreditation</option>
        </select>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Decision Notes</label>
        <textarea
          value={decisionNotes}
          onChange={(e) => setDecisionNotes(e.target.value)}
          style={textareaStyle}
          placeholder="Enter detailed notes about this decision..."
          required
        />
      </div>

      {(newStatus === 'suspended' || newStatus === 'withdrawn' || newStatus === 'rejected') && (
        <div style={fieldStyle}>
          <label style={labelStyle}>Reason</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={textareaStyle}
            placeholder="Enter specific reason for this action..."
            required
          />
        </div>
      )}

      <button type="submit" style={buttonStyle} disabled={loading}>
        {loading ? 'Processing...' : 'Submit Decision'}
      </button>
    </form>
  );
};

// ============================================================================
// ACCREDITATION CARD
// ============================================================================

export const AccreditationCard = ({ accreditation, onClick, type = 'cb' }) => {
  const cardStyle = {
    padding: '20px',
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    cursor: onClick ? 'pointer' : 'default',
    transition: 'all 0.2s',
    marginBottom: '16px'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px'
  };

  const numberStyle = {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#333',
    marginBottom: '4px'
  };

  const detailsStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    marginTop: '12px'
  };

  const detailItemStyle = {
    fontSize: '0.875rem',
    color: '#666'
  };

  const labelStyle = {
    fontWeight: '600',
    color: '#999',
    marginRight: '4px'
  };

  const scopesStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '12px'
  };

  const scopeBadgeStyle = {
    padding: '3px 8px',
    background: '#e3f2fd',
    color: '#1976d2',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '600'
  };

  return (
    <div
      style={cardStyle}
      onClick={onClick}
      onMouseEnter={(e) => {
        if (onClick) e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        if (onClick) e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={headerStyle}>
        <div>
          <div style={numberStyle}>{accreditation.accreditation_number}</div>
          <AccreditationStatusBadge status={accreditation.status} />
        </div>
        <div style={{ textAlign: 'right', fontSize: '0.875rem', color: '#666' }}>
          {formatDate(accreditation.application_date)}
        </div>
      </div>

      <div style={detailsStyle}>
        {accreditation.granted_date && (
          <div style={detailItemStyle}>
            <span style={labelStyle}>Granted:</span>
            {formatDate(accreditation.granted_date)}
          </div>
        )}
        {accreditation.expiry_date && (
          <div style={detailItemStyle}>
            <span style={labelStyle}>Expires:</span>
            {formatDate(accreditation.expiry_date)}
          </div>
        )}
        {accreditation.next_surveillance_due && (
          <div style={detailItemStyle}>
            <span style={labelStyle}>Next Surveillance:</span>
            {formatDate(accreditation.next_surveillance_due)}
          </div>
        )}
      </div>

      {accreditation.scopes && accreditation.scopes.length > 0 && (
        <div style={scopesStyle}>
          {accreditation.scopes.map((scope, index) => (
            <span key={index} style={scopeBadgeStyle}>
              {scope.scope?.scope_code || `Scope ${scope.scope_id}`}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// LOADING SPINNER
// ============================================================================

export const LoadingSpinner = () => {
  const spinnerStyle = {
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #3498db',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite',
    margin: '20px auto'
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={spinnerStyle}></div>
    </>
  );
};

// ============================================================================
// ERROR MESSAGE
// ============================================================================

export const ErrorMessage = ({ message }) => {
  const errorStyle = {
    padding: '16px',
    background: '#ffebee',
    color: '#c62828',
    border: '1px solid #ef9a9a',
    borderRadius: '4px',
    marginBottom: '16px'
  };

  return message ? <div style={errorStyle}>{message}</div> : null;
};

// ============================================================================
// SUCCESS MESSAGE
// ============================================================================

export const SuccessMessage = ({ message }) => {
  const successStyle = {
    padding: '16px',
    background: '#e8f5e9',
    color: '#2e7d32',
    border: '1px solid #a5d6a7',
    borderRadius: '4px',
    marginBottom: '16px'
  };

  return message ? <div style={successStyle}>{message}</div> : null;
};
